import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  GripVertical,
  Image as ImageIcon,
  Trash2,
  MoreHorizontal,
  ChevronUp,
  ChevronDown,
  Type,
  Bold,
  Italic,
  Underline,
  Link,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading2,
  Heading3,
  Tags,
  Sparkles,
  X,
  Loader2,
  Check,
  Star,
  RefreshCw,
} from 'lucide-react';
import { ImageSourceSelector, InsertableImage } from './ImageSourceSelector';
import { webhookService } from '../../services/api';

// ===============================
// TYPES
// ===============================
interface ArticleBlockEditorProps {
  html: string;
  onHtmlChange: (html: string) => void;
  projectId?: string;
  sessionId?: string;
  articleTitle?: string;
  brief?: any;
}

interface ContentBlock {
  id: string;
  type: 'heading' | 'text' | 'image';
  content: string;
  imageOrigin?: 'wp-media' | 'base64';
  wpId?: number;
}

// ===============================
// STYLES - Editor.js inspired
// ===============================
const editorStyles = `
.ce-block {
  position: relative;
  padding: 0.5rem 0;
}

.ce-block:hover .ce-toolbar {
  opacity: 1;
}

.ce-block:hover .ce-block__content {
  background-color: rgba(0, 0, 0, 0.02);
}

.ce-block--focused .ce-block__content {
  background-color: rgba(56, 182, 255, 0.05);
  box-shadow: inset 4px 0 0 0 #388AEB;
}

.ce-toolbar {
  position: absolute;
  left: -50px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ce-toolbar__button {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #707684;
  cursor: pointer;
  transition: all 0.15s ease;
}

.ce-toolbar__button:hover {
  background: #eff2f5;
  color: #388AEB;
}

.ce-toolbar__button--drag {
  cursor: grab;
}

.ce-toolbar__button--drag:active {
  cursor: grabbing;
}

.ce-block__content {
  border-radius: 8px;
  transition: all 0.15s ease;
}

.ce-block__settings {
  position: absolute;
  right: -40px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 0.15s ease;
}

.ce-block:hover .ce-block__settings {
  opacity: 1;
}

.ce-settings-menu {
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 3px 15px -3px rgba(13, 20, 33, 0.13), 0 0 0 1px rgba(0, 0, 0, 0.05);
  min-width: 180px;
  padding: 6px;
  z-index: 100;
}

.ce-settings-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  color: #1D202B;
  transition: background 0.1s ease;
}

.ce-settings-menu__item:hover {
  background: #F8F9FB;
}

.ce-settings-menu__item--danger {
  color: #E24A4A;
}

.ce-settings-menu__item--danger:hover {
  background: #FFECEC;
}

.ce-add-block {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 0;
  margin: 8px 0;
  border-radius: 8px;
  border: 2px dashed transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #707684;
}

.ce-add-block:hover {
  border-color: #388AEB;
  background: rgba(56, 138, 235, 0.05);
  color: #388AEB;
}

.ce-add-block--featured {
  border: 2px dashed #EAB308;
  background: linear-gradient(135deg, rgba(234, 179, 8, 0.08) 0%, rgba(253, 224, 71, 0.08) 100%);
  color: #92400E;
  padding: 16px 0;
}

.ce-add-block--featured:hover {
  border-color: #CA8A04;
  background: linear-gradient(135deg, rgba(234, 179, 8, 0.15) 0%, rgba(253, 224, 71, 0.15) 100%);
  color: #78350F;
}

.ce-image-block {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fb;
}

.ce-image-block img {
  display: block;
  max-width: 100%;
  margin: 0 auto;
}

.ce-image-block__badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  backdrop-filter: blur(8px);
}

.ce-image-block__badge--wp {
  background: rgba(34, 197, 94, 0.9);
  color: white;
}

.ce-image-block__badge--pending {
  background: rgba(245, 158, 11, 0.9);
  color: white;
}

.ce-image-block__badge--featured {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: linear-gradient(135deg, #EAB308 0%, #F59E0B 100%);
  color: white;
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(234, 179, 8, 0.4);
  z-index: 5;
}

/* Image metadata button */
.ce-image-block__meta-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(99, 102, 241, 0.9);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.ce-image-block__meta-btn:hover {
  background: rgba(99, 102, 241, 1);
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.ce-image-block__meta-btn--has-meta {
  background: rgba(34, 197, 94, 0.9);
}

.ce-image-block__meta-btn--has-meta:hover {
  background: rgba(34, 197, 94, 1);
}

/* Image metadata panel */
.ce-image-meta-panel {
  position: absolute;
  top: 50px;
  right: 12px;
  width: 320px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  z-index: 100;
  animation: ce-panel-appear 0.2s ease;
  overflow: hidden;
}

@keyframes ce-panel-appear {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ce-image-meta-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
}

.ce-image-meta-panel__header h4 {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ce-image-meta-panel__close {
  width: 24px;
  height: 24px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ce-image-meta-panel__close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.ce-image-meta-panel__body {
  padding: 16px;
}

.ce-image-meta-panel__field {
  margin-bottom: 16px;
}

.ce-image-meta-panel__field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ce-image-meta-panel__field input,
.ce-image-meta-panel__field textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  resize: none;
}

.ce-image-meta-panel__field input:focus,
.ce-image-meta-panel__field textarea:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.ce-image-meta-panel__field small {
  display: block;
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
}

.ce-image-meta-panel__actions {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

.ce-image-meta-panel__btn {
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.ce-image-meta-panel__btn--primary {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
}

.ce-image-meta-panel__btn--primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.ce-image-meta-panel__btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.ce-image-meta-panel__btn--secondary {
  background: white;
  color: #374151;
  border: 1px solid #e5e7eb;
}

.ce-image-meta-panel__btn--secondary:hover {
  background: #f3f4f6;
}

.ce-dragging {
  opacity: 0.5;
}

.ce-drag-overlay {
  background: white;
  border-radius: 10px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15), 0 0 0 2px #388AEB;
  transform: rotate(2deg);
}

/* Quick insert button on text blocks */
.ce-block--editable {
  position: relative;
}

.ce-block--editable .ce-quick-insert {
  position: absolute;
  left: -40px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: transparent;
  border: 2px dashed transparent;
  color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 5;
}

.ce-block--editable:hover .ce-quick-insert {
  background: #f9fafb;
  border-color: #d1d5db;
  color: #9ca3af;
}

.ce-block--editable .ce-quick-insert:hover {
  background: #388AEB;
  border-color: #388AEB;
  color: white;
  transform: translateY(-50%) scale(1.1);
}

/* Inline images within text */

/* Inline images within text */
.ce-inline-image {
  display: inline-block;
  max-width: 50%;
  margin: 8px;
  border-radius: 8px;
  cursor: move;
  transition: all 0.2s ease;
  vertical-align: middle;
  position: relative;
}

/* When inside wrapper, remove margin from image */
.ce-inline-image-wrapper .ce-inline-image {
  margin: 0;
  max-width: 100%;
}

.ce-inline-image:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  outline: 2px solid #388AEB;
}

/* Legacy support for images without wrapper */
.ce-inline-image.float-left:not(.ce-inline-image-wrapper .ce-inline-image) {
  float: left;
  margin: 8px 16px 8px 0;
}

.ce-inline-image.float-right:not(.ce-inline-image-wrapper .ce-inline-image) {
  float: right;
  margin: 8px 0 8px 16px;
}

.ce-inline-image.float-none:not(.ce-inline-image-wrapper .ce-inline-image) {
  display: block;
  float: none;
  margin: 16px auto;
  max-width: 100%;
}

/* Clearfix for content with floating images */
.ce-block--editable .ce-block__content::after {
  content: "";
  display: table;
  clear: both;
}

/* Selected inline image */
.ce-inline-image.selected {
  outline: 3px solid #388AEB;
  outline-offset: 2px;
}

/* Dragging inline image */
.ce-inline-image.dragging {
  opacity: 0.4;
  outline: 2px dashed #388AEB;
}

/* Drop indicator for inline image repositioning */
.ce-drop-indicator {
  display: inline-block;
  width: 4px;
  height: 1.5em;
  background: #388AEB;
  border-radius: 2px;
  vertical-align: middle;
  margin: 0 2px;
  animation: ce-pulse 0.8s ease-in-out infinite;
}

@keyframes ce-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Ghost image during drag */
.ce-drag-ghost {
  position: fixed;
  pointer-events: none;
  z-index: 9999;
  opacity: 0.8;
  transform: scale(0.5);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

/* Highlight contenteditable during drag over */
.ce-block--editable.drag-over .ce-block__content {
  background-color: rgba(56, 138, 235, 0.08);
  outline: 2px dashed rgba(56, 138, 235, 0.5);
  outline-offset: -2px;
}

/* Context Menu */
.ce-context-menu {
  position: fixed;
  background: white;
  border-radius: 10px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  padding: 6px;
  z-index: 1000;
  min-width: 200px;
  animation: ce-menu-appear 0.15s ease;
}

@keyframes ce-menu-appear {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.ce-context-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 6px;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  transition: background 0.1s ease;
}

.ce-context-menu__item:hover {
  background: #f3f4f6;
}

.ce-context-menu__item--primary {
  color: #388AEB;
}

.ce-context-menu__item--primary:hover {
  background: #eff6ff;
}

.ce-context-menu__divider {
  height: 1px;
  background: #e5e7eb;
  margin: 4px 0;
}

/* Image position controls (appears on image selection) */
.ce-image-controls {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: #1e1e1e;
  border-radius: 8px;
  padding: 4px;
  display: flex;
  gap: 2px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 100;
}

.ce-image-controls button {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #a0a0a0;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.1s ease;
}

.ce-image-controls button:hover {
  background: #333;
  color: white;
}

.ce-image-controls button.active {
  background: #388AEB;
  color: white;
}

/* Image block specific styles */
.ce-block--image {
  margin: 16px 0;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: all 0.2s ease;
}

.ce-block--image:hover {
  border-color: #e5e7eb;
  background: #fafafa;
}

.ce-block--image .ce-toolbar {
  left: -54px;
}

.ce-block--image .ce-block__settings {
  right: -44px;
}

/* Editable text blocks */
.ce-block--editable .ce-block__content {
  cursor: text;
}

.ce-block--editable:focus-within .ce-block__content {
  background-color: rgba(56, 182, 255, 0.03);
  outline: none;
}

.ce-block--editable [contenteditable] {
  outline: none;
  min-height: 1em;
}

.ce-block--editable [contenteditable]:empty::before {
  content: 'Escribe algo...';
  color: #9ca3af;
  pointer-events: none;
}

/* Floating Toolbar */
.ce-floating-toolbar {
  position: fixed;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background: #1e1e1e;
  border-radius: 8px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1);
  z-index: 1000;
  transform: translateX(-50%);
  animation: ce-toolbar-appear 0.15s ease;
}

@keyframes ce-toolbar-appear {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.ce-floating-toolbar__button {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #a0a0a0;
  cursor: pointer;
  transition: all 0.1s ease;
}

.ce-floating-toolbar__button:hover {
  background: #333;
  color: white;
}

.ce-floating-toolbar__button--active {
  background: #388AEB;
  color: white;
}

.ce-floating-toolbar__button--active:hover {
  background: #2a7bd8;
}

.ce-floating-toolbar__divider {
  width: 1px;
  height: 20px;
  background: #444;
  margin: 0 4px;
}

/* Link input popup */
.ce-link-input {
  position: fixed;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #1e1e1e;
  border-radius: 8px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
  z-index: 1001;
  transform: translateX(-50%);
}

.ce-link-input input {
  width: 250px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #444;
  background: #333;
  color: white;
  font-size: 14px;
  outline: none;
}

.ce-link-input input:focus {
  border-color: #388AEB;
}

.ce-link-input button {
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  background: #388AEB;
  color: white;
  font-size: 14px;
  cursor: pointer;
}

.ce-link-input button:hover {
  background: #2a7bd8;
}
`;

// ===============================
// IMAGE METADATA PANEL COMPONENT
// ===============================
interface ImageMetadataPanelProps {
  blockId: string;
  currentTitle: string;
  currentAlt: string;
  articleTitle: string;
  articleContext: string;
  imageIndex: number;
  imageSrc: string;
  onSave: (title: string, alt: string) => void;
  onClose: () => void;
}

const ImageMetadataPanel: React.FC<ImageMetadataPanelProps> = ({
  currentTitle,
  currentAlt,
  articleTitle,
  articleContext,
  imageIndex,
  imageSrc,
  onSave,
  onClose,
}) => {
  const [title, setTitle] = useState(currentTitle);
  const [alt, setAlt] = useState(currentAlt);
  const [isGenerating, setIsGenerating] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const isGeneratingRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    isGeneratingRef.current = isGenerating;
  }, [isGenerating]);

  // Close on click outside - but NOT while generating
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Don't close if we're generating with AI
      if (isGeneratingRef.current) return;
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleGenerateWithAI = async () => {
    setIsGenerating(true);
    try {
      const result = await webhookService.generateImageMetadata(
        articleTitle,
        articleContext,
        imageIndex,
        imageSrc
      );
      if (result.title) setTitle(result.title);
      if (result.alt) setAlt(result.alt);
    } catch (error) {
      // Error generating metadata
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = () => {
    onSave(title, alt);
    onClose();
  };

  return (
    <div ref={panelRef} className="ce-image-meta-panel">
      <div className="ce-image-meta-panel__header">
        <h4>
          <Tags className="w-4 h-4" />
          Metadatos de Imagen
        </h4>
        <button className="ce-image-meta-panel__close" onClick={onClose}>
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="ce-image-meta-panel__body">
        <div className="ce-image-meta-panel__field">
          <label>Title (Título)</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Título descriptivo de la imagen"
            maxLength={60}
          />
          <small>{title.length}/60 caracteres</small>
        </div>

        <div className="ce-image-meta-panel__field">
          <label>Alt (Texto alternativo)</label>
          <textarea
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            placeholder="Descripción accesible de la imagen"
            rows={3}
            maxLength={125}
          />
          <small>{alt.length}/125 caracteres • Importante para SEO y accesibilidad</small>
        </div>
      </div>

      <div className="ce-image-meta-panel__actions">
        <button
          className="ce-image-meta-panel__btn ce-image-meta-panel__btn--primary"
          onClick={handleGenerateWithAI}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Generando...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Generar con IA
            </>
          )}
        </button>
        <button
          className="ce-image-meta-panel__btn ce-image-meta-panel__btn--secondary"
          onClick={handleSave}
        >
          <Check className="w-4 h-4" />
          Guardar
        </button>
      </div>
    </div>
  );
};

// ===============================
// HELPER FUNCTIONS
// ===============================
const detectImageOrigin = (element: Element): { origin: 'wp-media' | 'base64'; wpId?: number } => {
  const dataOrigin = element.getAttribute('data-origin');
  const dataWpId = element.getAttribute('data-wp-id');
  const src = element.getAttribute('src') || '';

  if (dataOrigin === 'wp-media' || dataOrigin === 'base64') {
    return { origin: dataOrigin, wpId: dataWpId ? parseInt(dataWpId) : undefined };
  }

  if (src.includes('/api/proxy-image') || src.includes('?id=') || src.includes('&id=')) {
    const idMatch = src.match(/[?&]id=(\d+)/);
    return { origin: 'wp-media', wpId: idMatch ? parseInt(idMatch[1]) : undefined };
  }

  if (src.startsWith('http://') || src.startsWith('https://')) {
    return { origin: 'wp-media' };
  }

  if (src.startsWith('data:image')) {
    return { origin: 'base64' };
  }

  return { origin: 'base64' };
};

// Procesa HTML para que todas las imágenes externas usen el proxy (evita ERR_HTTP2_PROTOCOL_ERROR)
const proxyImagesInHtml = (html: string): string => {
  if (!html) return '';
  return html.replace(
    /<img([^>]*)\ssrc=["'](https?:\/\/[^"']+)["']([^>]*)>/gi,
    (match, before, url, after) => {
      if (url.includes('/api/proxy-image')) return match;
      const proxiedUrl = `/api/proxy-image?url=${encodeURIComponent(url)}`;
      return `<img${before} src="${proxiedUrl}"${after}>`;
    }
  );
};

// Create inline image element (simple, without wrapper - metadata stored in attributes)
const createInlineImageElement = (
  src: string,
  alt: string,
  title: string,
  origin: string,
  wpId: number | undefined,
  positionClass: string,
  maxWidth: string
): HTMLImageElement => {
  const img = document.createElement('img');
  img.src = src;
  img.alt = alt || '';
  img.title = title || '';
  img.setAttribute('data-origin', origin);
  if (wpId) img.setAttribute('data-wp-id', String(wpId));
  img.className = `ce-inline-image ${positionClass}`;
  img.style.maxWidth = maxWidth;
  img.style.borderRadius = '8px';
  img.setAttribute('draggable', 'true');
  return img;
};

// No-op function for backward compatibility (badge removed)
const updateInlineImageBadge = (_imgElement: HTMLImageElement) => {
  // Badge removed - metadata status shown in InlineImageControls button color
};

const parseHtmlToBlocks = (html: string): ContentBlock[] => {
  if (!html?.trim()) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const blocks: ContentBlock[] = [];
  let counter = 0;

  Array.from(doc.body.children).forEach((element) => {
    const tagName = element.tagName.toUpperCase();
    const imgElement = tagName === 'IMG' ? element : element.querySelector('img');

    if (imgElement) {
      const { origin, wpId } = detectImageOrigin(imgElement);
      blocks.push({
        id: `block-${counter++}-${Date.now()}`,
        type: 'image',
        content: element.outerHTML,
        imageOrigin: origin,
        wpId,
      });
    } else if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(tagName)) {
      blocks.push({
        id: `block-${counter++}-${Date.now()}`,
        type: 'heading',
        content: element.outerHTML,
      });
    } else if (element.textContent?.trim()) {
      blocks.push({
        id: `block-${counter++}-${Date.now()}`,
        type: 'text',
        content: element.outerHTML,
      });
    }
  });

  return blocks;
};

const blocksToHtml = (blocks: ContentBlock[]): string => {
  return blocks.map((block) => block.content).join('\n');
};

const createImageHtml = (image: InsertableImage): string => {
  const attrs = [
    `src="${image.src}"`,
    `alt="${image.alt || ''}"`,
    `title="${image.title || ''}"`,
    `data-origin="${image.origin}"`,
    image.wpId ? `data-wp-id="${image.wpId}"` : '',
    'style="display:block; max-width:100%; margin:0 auto; border-radius:4px;"',
  ].filter(Boolean).join(' ');

  return `<figure style="margin:24px 0; text-align:center;"><img ${attrs} /></figure>`;
};

// ===============================
// SORTABLE BLOCK COMPONENT
// ===============================
interface SortableBlockProps {
  block: ContentBlock;
  index: number;
  totalBlocks: number;
  imageIndex: number; // Index among image blocks only
  onDelete: (id: string) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onInsertAfter: (index: number) => void;
  onContentChange: (id: string, newContent: string) => void;
  onSelectionChange: (rect: DOMRect | null) => void;
  onContextMenu: (e: React.MouseEvent, blockId: string) => void;
  onInlineImageClick: (e: React.MouseEvent, blockId: string) => void;
  // Native drag and drop for inline images
  onInlineImageDragStart: (e: React.DragEvent, blockId: string) => void;
  onInlineImageDragOver: (e: React.DragEvent, blockId: string) => void;
  onInlineImageDragLeave: (e: React.DragEvent) => void;
  onInlineImageDrop: (e: React.DragEvent, blockId: string) => void;
  onInlineImageDragEnd: (e: React.DragEvent) => void;
  // Image metadata
  articleTitle: string;
  articleContext: string;
  onUpdateImageMetadata: (blockId: string, title: string, alt: string) => void;
  // Replace featured image
  onReplaceFeatured?: (blockId: string) => void;
}

const SortableBlock: React.FC<SortableBlockProps> = ({
  block,
  index,
  totalBlocks,
  imageIndex,
  onDelete,
  onMoveUp,
  onMoveDown,
  onInsertAfter,
  onContentChange,
  onSelectionChange,
  onContextMenu,
  onInlineImageClick,
  onInlineImageDragStart,
  onInlineImageDragOver,
  onInlineImageDragLeave,
  onInlineImageDrop,
  onInlineImageDragEnd,
  articleTitle,
  articleContext,
  onUpdateImageMetadata,
  onReplaceFeatured,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showMetadataPanel, setShowMetadataPanel] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isImage = block.type === 'image';
  const isEditable = !isImage;

  // Extract current image metadata from content
  const getImageMetadata = useCallback(() => {
    if (!isImage) return { title: '', alt: '', src: '' };
    const parser = new DOMParser();
    const doc = parser.parseFromString(block.content, 'text/html');
    const img = doc.querySelector('img');
    return {
      title: img?.getAttribute('title') || '',
      alt: img?.getAttribute('alt') || '',
      src: img?.getAttribute('src') || '',
    };
  }, [block.content, isImage]);

  const { title: currentTitle, alt: currentAlt, src: imageSrc } = getImageMetadata();
  const hasMetadata = currentTitle || currentAlt;

  // Featured image (first image, imageIndex === 1) should not be draggable
  const isFeaturedImage = isImage && imageIndex === 1;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: block.id,
    disabled: !isImage || isFeaturedImage, // Only non-featured images are draggable
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  // Handle content editing
  const handleBlur = useCallback(() => {
    if (contentRef.current && isEditable) {
      const newContent = contentRef.current.innerHTML;
      if (newContent !== block.content) {
        onContentChange(block.id, newContent);
      }
    }
    onSelectionChange(null);
  }, [block.id, block.content, isEditable, onContentChange, onSelectionChange]);

  // Handle text selection for toolbar positioning
  const handleSelect = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      onSelectionChange(rect);
    } else {
      onSelectionChange(null);
    }
  }, [onSelectionChange]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          document.execCommand('bold', false);
          break;
        case 'i':
          e.preventDefault();
          document.execCommand('italic', false);
          break;
        case 'u':
          e.preventDefault();
          document.execCommand('underline', false);
          break;
      }
    }
  }, []);

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-block-id={block.id}
      className={`ce-block ${isDragging ? 'ce-dragging' : ''} ${isImage ? 'ce-block--image' : ''} ${isEditable ? 'ce-block--editable' : ''}`}
      onDragOver={isEditable ? (e) => onInlineImageDragOver(e, block.id) : undefined}
      onDragLeave={isEditable ? onInlineImageDragLeave : undefined}
      onDrop={isEditable ? (e) => onInlineImageDrop(e, block.id) : undefined}
    >
      {/* Left Toolbar - Only for non-featured images */}
      {isImage && !isFeaturedImage && (
        <div className="ce-toolbar">
          {/* Drag Handle */}
          <button
            {...attributes}
            {...listeners}
            className="ce-toolbar__button ce-toolbar__button--drag"
            title="Arrastrar para mover"
          >
            <GripVertical className="w-5 h-5" />
          </button>

          {/* Add Block Button */}
          <button
            className="ce-toolbar__button"
            onClick={() => onInsertAfter(index)}
            title="Añadir imagen"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Block Content */}
      <div className="ce-block__content">
        {isImage ? (
          <div className="ce-image-block group">
            {/* Featured Image Badge - Only for first image */}
            {imageIndex === 1 && (
              <div className="ce-image-block__badge--featured">
                <Star className="w-3.5 h-3.5 fill-white" />
                Imagen Destacada
              </div>
            )}

            {/* Replace Featured Image Button - Only for first image */}
            {isFeaturedImage && onReplaceFeatured && (
              <button
                onClick={() => onReplaceFeatured(block.id)}
                className="absolute top-12 right-3 z-10 px-3 py-1.5 bg-white/90 hover:bg-white text-gray-700 text-xs font-medium rounded-full shadow-md transition-all opacity-0 group-hover:opacity-100 flex items-center gap-1.5"
                title="Cambiar imagen destacada"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Cambiar
              </button>
            )}

            {/* Origin Badge */}
            <div
              className={`ce-image-block__badge ${
                block.imageOrigin === 'wp-media'
                  ? 'ce-image-block__badge--wp'
                  : 'ce-image-block__badge--pending'
              }`}
            >
              {block.imageOrigin === 'wp-media' ? '✓ WordPress' : '↑ Pendiente'}
            </div>

            {/* Metadata Button */}
            <button
              className={`ce-image-block__meta-btn ${hasMetadata ? 'ce-image-block__meta-btn--has-meta' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowMetadataPanel(!showMetadataPanel);
              }}
              title={hasMetadata ? 'Editar metadatos (title/alt)' : 'Añadir metadatos SEO'}
            >
              <Tags className="w-4 h-4" />
            </button>

            {/* Metadata Panel */}
            {showMetadataPanel && (
              <ImageMetadataPanel
                blockId={block.id}
                currentTitle={currentTitle}
                currentAlt={currentAlt}
                articleTitle={articleTitle}
                articleContext={articleContext}
                imageIndex={imageIndex}
                imageSrc={imageSrc}
                onSave={(newTitle, newAlt) => {
                  onUpdateImageMetadata(block.id, newTitle, newAlt);
                }}
                onClose={() => setShowMetadataPanel(false)}
              />
            )}

            {/* Image */}
            <div
              className="p-4"
              dangerouslySetInnerHTML={{ __html: proxyImagesInHtml(block.content) }}
            />
          </div>
        ) : block.type === 'heading' ? (
          <div
            ref={contentRef}
            contentEditable
            suppressContentEditableWarning
            className="prose prose-lg max-w-none px-4 py-2 [&_h1]:text-3xl [&_h1]:font-bold [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-xl [&_h3]:font-semibold [&_.ce-inline-image]:cursor-move [&_.ce-inline-image[draggable]]:cursor-grab"
            dangerouslySetInnerHTML={{ __html: proxyImagesInHtml(block.content) }}
            onBlur={handleBlur}
            onMouseUp={handleSelect}
            onKeyUp={handleSelect}
            onKeyDown={handleKeyDown}
            onContextMenu={(e) => onContextMenu(e, block.id)}
            onClick={(e) => onInlineImageClick(e, block.id)}
            onDragStart={(e) => onInlineImageDragStart(e, block.id)}
            onDragEnd={onInlineImageDragEnd}
          />
        ) : (
          <div
            ref={contentRef}
            contentEditable
            suppressContentEditableWarning
            className="prose prose-lg max-w-none px-4 py-2 [&_p]:text-gray-700 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-blue-600 [&_.ce-inline-image]:cursor-move [&_.ce-inline-image[draggable]]:cursor-grab"
            dangerouslySetInnerHTML={{ __html: proxyImagesInHtml(block.content) }}
            onBlur={handleBlur}
            onMouseUp={handleSelect}
            onKeyUp={handleSelect}
            onKeyDown={handleKeyDown}
            onContextMenu={(e) => onContextMenu(e, block.id)}
            onClick={(e) => onInlineImageClick(e, block.id)}
            onDragStart={(e) => onInlineImageDragStart(e, block.id)}
            onDragEnd={onInlineImageDragEnd}
          />
        )}
      </div>

      {/* Right Settings Button - Hidden for featured image */}
      {isImage && !isFeaturedImage && (
        <div className="ce-block__settings" ref={menuRef}>
          <button
            className="ce-toolbar__button"
            onClick={() => setShowMenu(!showMenu)}
          >
            <MoreHorizontal className="w-5 h-5" />
          </button>

          {/* Settings Menu */}
          {showMenu && (
            <div className="ce-settings-menu">
              <button
                className="ce-settings-menu__item"
                onClick={() => {
                  onMoveUp(index);
                  setShowMenu(false);
                }}
                disabled={index === 0}
              >
                <ChevronUp className="w-4 h-4" />
                <span>Mover arriba</span>
              </button>

              <button
                className="ce-settings-menu__item"
                onClick={() => {
                  onMoveDown(index);
                  setShowMenu(false);
                }}
                disabled={index === totalBlocks - 1}
              >
                <ChevronDown className="w-4 h-4" />
                <span>Mover abajo</span>
              </button>

              <div className="my-1 border-t border-gray-100" />

              <button
                className="ce-settings-menu__item ce-settings-menu__item--danger"
                onClick={() => {
                  onDelete(block.id);
                  setShowMenu(false);
                }}
              >
                <Trash2 className="w-4 h-4" />
                <span>Eliminar imagen</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ===============================
// DRAG OVERLAY
// ===============================
const DragOverlayContent: React.FC<{ block: ContentBlock }> = ({ block }) => {
  return (
    <div className="ce-drag-overlay max-w-md p-4">
      {block.type === 'image' ? (
        <div
          className="[&_img]:max-h-32 [&_img]:w-auto [&_img]:mx-auto"
          dangerouslySetInnerHTML={{ __html: proxyImagesInHtml(block.content) }}
        />
      ) : (
        <div className="flex items-center gap-2 text-gray-600">
          <Type className="w-5 h-5" />
          <span className="truncate max-w-xs">
            {block.content.replace(/<[^>]*>/g, '').slice(0, 50)}...
          </span>
        </div>
      )}
    </div>
  );
};

// ===============================
// CONTEXT MENU - For inline image and block insertion
// ===============================
interface ContextMenuProps {
  position: { x: number; y: number } | null;
  onInsertInline: (position: 'left' | 'right' | 'center') => void;
  onClose: () => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ position, onInsertInline, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (position) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [position, onClose]);

  if (!position) return null;

  return (
    <div
      ref={menuRef}
      className="ce-context-menu"
      style={{ left: position.x, top: position.y }}
    >
      <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
        Insertar imagen
      </div>
      <button
        className="ce-context-menu__item ce-context-menu__item--primary"
        onClick={() => onInsertInline('left')}
      >
        <AlignLeft className="w-4 h-4" />
        <span>Imagen a la izquierda</span>
      </button>
      <button
        className="ce-context-menu__item ce-context-menu__item--primary"
        onClick={() => onInsertInline('right')}
      >
        <AlignRight className="w-4 h-4" />
        <span>Imagen a la derecha</span>
      </button>
      <button
        className="ce-context-menu__item ce-context-menu__item--primary"
        onClick={() => onInsertInline('center')}
      >
        <AlignCenter className="w-4 h-4" />
        <span>Imagen centrada</span>
      </button>
      <div className="ce-context-menu__divider" />
      <button
        className="ce-context-menu__item"
        onClick={onClose}
      >
        <span>Cancelar</span>
      </button>
    </div>
  );
};

// ===============================
// INLINE IMAGE CONTROLS - Shows when clicking an inline image
// ===============================
interface InlineImageControlsProps {
  position: { x: number; y: number };
  currentPosition: 'left' | 'right' | 'center';
  onChangePosition: (position: 'left' | 'right' | 'center') => void;
  onDelete: () => void;
  onClose: () => void;
  onEditMetadata: () => void;
  hasMetadata: boolean;
  metadataPanelOpen?: boolean;
}

const InlineImageControls: React.FC<InlineImageControlsProps> = ({
  position,
  currentPosition,
  onChangePosition,
  onDelete,
  onClose,
  onEditMetadata,
  hasMetadata,
  metadataPanelOpen = false,
}) => {
  const controlsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Don't close if metadata panel is open - let the panel handle its own closing
      if (metadataPanelOpen) return;

      // Check if click is inside the metadata panel (class ce-image-meta-panel)
      const target = e.target as HTMLElement;
      if (target.closest('.ce-image-meta-panel')) return;

      if (controlsRef.current && !controlsRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, metadataPanelOpen]);

  return (
    <div
      ref={controlsRef}
      className="ce-image-controls"
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y - 50,
        transform: 'translateX(-50%)',
      }}
    >
      <button
        className={currentPosition === 'left' ? 'active' : ''}
        onClick={() => onChangePosition('left')}
        title="Alinear izquierda"
      >
        <AlignLeft className="w-4 h-4" />
      </button>
      <button
        className={currentPosition === 'center' ? 'active' : ''}
        onClick={() => onChangePosition('center')}
        title="Centrar"
      >
        <AlignCenter className="w-4 h-4" />
      </button>
      <button
        className={currentPosition === 'right' ? 'active' : ''}
        onClick={() => onChangePosition('right')}
        title="Alinear derecha"
      >
        <AlignRight className="w-4 h-4" />
      </button>
      <div style={{ width: '1px', height: '20px', background: '#444', margin: '0 4px' }} />
      <button
        onClick={onEditMetadata}
        title={hasMetadata ? 'Editar metadatos (title/alt)' : 'Añadir metadatos SEO'}
        style={{ color: hasMetadata ? '#22c55e' : '#6366f1' }}
      >
        <Tags className="w-4 h-4" />
      </button>
      <div style={{ width: '1px', height: '20px', background: '#444', margin: '0 4px' }} />
      <button
        onClick={onDelete}
        title="Eliminar imagen"
        style={{ color: '#ef4444' }}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

// ===============================
// FLOATING TOOLBAR COMPONENT
// ===============================
interface FloatingToolbarProps {
  position: { x: number; y: number } | null;
  onFormat: (command: string, value?: string) => void;
  activeFormats: Set<string>;
}

const FloatingToolbar: React.FC<FloatingToolbarProps> = ({ position, onFormat, activeFormats }) => {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const linkInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showLinkInput && linkInputRef.current) {
      linkInputRef.current.focus();
    }
  }, [showLinkInput]);

  if (!position) return null;

  const handleLinkSubmit = () => {
    if (linkUrl.trim()) {
      onFormat('createLink', linkUrl);
      setLinkUrl('');
      setShowLinkInput(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLinkSubmit();
    } else if (e.key === 'Escape') {
      setShowLinkInput(false);
      setLinkUrl('');
    }
  };

  if (showLinkInput) {
    return (
      <div
        className="ce-link-input"
        style={{ left: position.x, top: position.y - 50 }}
      >
        <input
          ref={linkInputRef}
          type="text"
          placeholder="https://example.com"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button onClick={handleLinkSubmit}>Añadir</button>
        <button
          onClick={() => {
            setShowLinkInput(false);
            setLinkUrl('');
          }}
          style={{ background: '#555' }}
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div
      className="ce-floating-toolbar"
      style={{ left: position.x, top: position.y - 50 }}
    >
      {/* Text Style Buttons */}
      <button
        className={`ce-floating-toolbar__button ${activeFormats.has('bold') ? 'ce-floating-toolbar__button--active' : ''}`}
        onClick={() => onFormat('bold')}
        title="Negrita (Ctrl+B)"
      >
        <Bold className="w-4 h-4" />
      </button>
      <button
        className={`ce-floating-toolbar__button ${activeFormats.has('italic') ? 'ce-floating-toolbar__button--active' : ''}`}
        onClick={() => onFormat('italic')}
        title="Cursiva (Ctrl+I)"
      >
        <Italic className="w-4 h-4" />
      </button>
      <button
        className={`ce-floating-toolbar__button ${activeFormats.has('underline') ? 'ce-floating-toolbar__button--active' : ''}`}
        onClick={() => onFormat('underline')}
        title="Subrayado (Ctrl+U)"
      >
        <Underline className="w-4 h-4" />
      </button>

      <div className="ce-floating-toolbar__divider" />

      {/* Link */}
      <button
        className={`ce-floating-toolbar__button ${activeFormats.has('link') ? 'ce-floating-toolbar__button--active' : ''}`}
        onClick={() => {
          if (activeFormats.has('link')) {
            onFormat('unlink');
          } else {
            setShowLinkInput(true);
          }
        }}
        title="Enlace (Ctrl+K)"
      >
        <Link className="w-4 h-4" />
      </button>

      <div className="ce-floating-toolbar__divider" />

      {/* Headings */}
      <button
        className={`ce-floating-toolbar__button ${activeFormats.has('h2') ? 'ce-floating-toolbar__button--active' : ''}`}
        onClick={() => onFormat('formatBlock', 'h2')}
        title="Título H2"
      >
        <Heading2 className="w-4 h-4" />
      </button>
      <button
        className={`ce-floating-toolbar__button ${activeFormats.has('h3') ? 'ce-floating-toolbar__button--active' : ''}`}
        onClick={() => onFormat('formatBlock', 'h3')}
        title="Subtítulo H3"
      >
        <Heading3 className="w-4 h-4" />
      </button>

      <div className="ce-floating-toolbar__divider" />

      {/* Lists */}
      <button
        className={`ce-floating-toolbar__button ${activeFormats.has('ul') ? 'ce-floating-toolbar__button--active' : ''}`}
        onClick={() => onFormat('insertUnorderedList')}
        title="Lista con viñetas"
      >
        <List className="w-4 h-4" />
      </button>
      <button
        className={`ce-floating-toolbar__button ${activeFormats.has('ol') ? 'ce-floating-toolbar__button--active' : ''}`}
        onClick={() => onFormat('insertOrderedList')}
        title="Lista numerada"
      >
        <ListOrdered className="w-4 h-4" />
      </button>

      <div className="ce-floating-toolbar__divider" />

      {/* Alignment */}
      <button
        className={`ce-floating-toolbar__button ${activeFormats.has('justifyLeft') ? 'ce-floating-toolbar__button--active' : ''}`}
        onClick={() => onFormat('justifyLeft')}
        title="Alinear izquierda"
      >
        <AlignLeft className="w-4 h-4" />
      </button>
      <button
        className={`ce-floating-toolbar__button ${activeFormats.has('justifyCenter') ? 'ce-floating-toolbar__button--active' : ''}`}
        onClick={() => onFormat('justifyCenter')}
        title="Centrar"
      >
        <AlignCenter className="w-4 h-4" />
      </button>
      <button
        className={`ce-floating-toolbar__button ${activeFormats.has('justifyRight') ? 'ce-floating-toolbar__button--active' : ''}`}
        onClick={() => onFormat('justifyRight')}
        title="Alinear derecha"
      >
        <AlignRight className="w-4 h-4" />
      </button>
    </div>
  );
};

// ===============================
// MAIN EDITOR COMPONENT
// ===============================
export const ArticleBlockEditor: React.FC<ArticleBlockEditorProps> = ({
  html,
  onHtmlChange,
  projectId,
  sessionId,
  articleTitle,
  brief,
}) => {
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [insertAtIndex, setInsertAtIndex] = useState<number>(0);
  const [isInsertingFeatured, setIsInsertingFeatured] = useState(false);
  const [replaceBlockId, setReplaceBlockId] = useState<string | null>(null);

  // Floating toolbar state
  const [toolbarPosition, setToolbarPosition] = useState<{ x: number; y: number } | null>(null);
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());

  // Context menu state for inline image insertion
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [inlineImagePosition, setInlineImagePosition] = useState<'left' | 'right' | 'center'>('left');

  // Selected inline image state
  const [selectedInlineImage, setSelectedInlineImage] = useState<{
    element: HTMLImageElement;
    blockId: string;
    position: { x: number; y: number };
  } | null>(null);

  // Inline image metadata panel state
  const [showInlineMetadataPanel, setShowInlineMetadataPanel] = useState(false);

  const htmlRef = useRef(html);

  // Inject styles
  useEffect(() => {
    const styleId = 'ce-editor-styles';
    if (!document.getElementById(styleId)) {
      const styleTag = document.createElement('style');
      styleTag.id = styleId;
      styleTag.textContent = editorStyles;
      document.head.appendChild(styleTag);
    }
    return () => {
      const tag = document.getElementById(styleId);
      if (tag) tag.remove();
    };
  }, []);

  // Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Parse HTML on mount
  useEffect(() => {
    const parsed = parseHtmlToBlocks(html);
    setBlocks(parsed);
    htmlRef.current = html;
  }, []);

  // Sync from external HTML changes
  useEffect(() => {
    if (html !== htmlRef.current) {
      const parsed = parseHtmlToBlocks(html);
      setBlocks(parsed);
      htmlRef.current = html;
    }
  }, [html]);

  // Sync to HTML
  const syncToHtml = useCallback(
    (newBlocks: ContentBlock[]) => {
      const newHtml = blocksToHtml(newBlocks);
      htmlRef.current = newHtml;
      onHtmlChange(newHtml);
    },
    [onHtmlChange]
  );

  // Drag handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newBlocks = arrayMove(blocks, oldIndex, newIndex);
      setBlocks(newBlocks);
      syncToHtml(newBlocks);
    }
  };

  // Block actions
  const handleDelete = useCallback(
    (blockId: string) => {
      const newBlocks = blocks.filter((b) => b.id !== blockId);
      setBlocks(newBlocks);
      syncToHtml(newBlocks);
    },
    [blocks, syncToHtml]
  );

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index <= 0) return;
      const newBlocks = arrayMove(blocks, index, index - 1);
      setBlocks(newBlocks);
      syncToHtml(newBlocks);
    },
    [blocks, syncToHtml]
  );

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index >= blocks.length - 1) return;
      const newBlocks = arrayMove(blocks, index, index + 1);
      setBlocks(newBlocks);
      syncToHtml(newBlocks);
    },
    [blocks, syncToHtml]
  );

  const handleInsertAfter = (index: number) => {
    setInsertAtIndex(index + 1);
    setIsInsertingFeatured(false);
    setSelectorOpen(true);
  };

  // Insert featured image at the beginning
  const handleInsertFeatured = () => {
    setInsertAtIndex(0);
    setIsInsertingFeatured(true);
    setSelectorOpen(true);
  };

  // Replace an existing featured image
  const handleReplaceFeatured = (blockId: string) => {
    setReplaceBlockId(blockId);
    setIsInsertingFeatured(true);
    setSelectorOpen(true);
  };

  const handleImageSelect = useCallback(
    (image: InsertableImage) => {
      const imageHtml = createImageHtml(image);

      // If replacing an existing block
      if (replaceBlockId) {
        const newBlocks = blocks.map((block) =>
          block.id === replaceBlockId
            ? {
                ...block,
                content: imageHtml,
                imageOrigin: image.origin,
                wpId: image.wpId,
              }
            : block
        );
        setBlocks(newBlocks);
        syncToHtml(newBlocks);
        setSelectorOpen(false);
        setReplaceBlockId(null);
        return;
      }

      // Otherwise insert a new block
      const newBlock: ContentBlock = {
        id: `block-img-${Date.now()}`,
        type: 'image',
        content: imageHtml,
        imageOrigin: image.origin,
        wpId: image.wpId,
      };

      const newBlocks = [...blocks];
      newBlocks.splice(insertAtIndex, 0, newBlock);
      setBlocks(newBlocks);
      syncToHtml(newBlocks);
      setSelectorOpen(false);
    },
    [blocks, insertAtIndex, replaceBlockId, syncToHtml]
  );

  // Handle content change from editable blocks
  const handleContentChange = useCallback(
    (blockId: string, newContent: string) => {
      const newBlocks = blocks.map((block) =>
        block.id === blockId ? { ...block, content: newContent } : block
      );
      setBlocks(newBlocks);
      syncToHtml(newBlocks);
    },
    [blocks, syncToHtml]
  );

  // Handle image metadata update (title and alt)
  const handleUpdateImageMetadata = useCallback(
    (blockId: string, newTitle: string, newAlt: string) => {
      const newBlocks = blocks.map((block) => {
        if (block.id !== blockId || block.type !== 'image') return block;

        // Parse the content and update image attributes
        const parser = new DOMParser();
        const doc = parser.parseFromString(block.content, 'text/html');
        const img = doc.querySelector('img');

        if (img) {
          img.setAttribute('title', newTitle);
          img.setAttribute('alt', newAlt);
          // Get the updated HTML (the body content, not the full document)
          const updatedContent = doc.body.innerHTML;
          return { ...block, content: updatedContent };
        }

        return block;
      });

      setBlocks(newBlocks);
      syncToHtml(newBlocks);
    },
    [blocks, syncToHtml]
  );

  // Handle selection change for floating toolbar
  const handleSelectionChange = useCallback((rect: DOMRect | null) => {
    if (rect) {
      setToolbarPosition({
        x: rect.left + rect.width / 2,
        y: rect.top,
      });
      // Check active formats
      const formats = new Set<string>();
      if (document.queryCommandState('bold')) formats.add('bold');
      if (document.queryCommandState('italic')) formats.add('italic');
      if (document.queryCommandState('underline')) formats.add('underline');
      if (document.queryCommandState('insertUnorderedList')) formats.add('ul');
      if (document.queryCommandState('insertOrderedList')) formats.add('ol');
      if (document.queryCommandState('justifyLeft')) formats.add('justifyLeft');
      if (document.queryCommandState('justifyCenter')) formats.add('justifyCenter');
      if (document.queryCommandState('justifyRight')) formats.add('justifyRight');

      // Check if inside a link
      const selection = window.getSelection();
      if (selection && selection.anchorNode) {
        let node: Node | null = selection.anchorNode;
        while (node && node !== document.body) {
          if (node.nodeName === 'A') {
            formats.add('link');
            break;
          }
          node = node.parentNode;
        }
        // Check heading
        node = selection.anchorNode;
        while (node && node !== document.body) {
          if (node.nodeName === 'H2') formats.add('h2');
          if (node.nodeName === 'H3') formats.add('h3');
          node = node.parentNode;
        }
      }

      setActiveFormats(formats);
    } else {
      setToolbarPosition(null);
    }
  }, []);

  // Handle format command from toolbar
  const handleFormat = useCallback((command: string, value?: string) => {
    if (command === 'formatBlock' && value) {
      document.execCommand('formatBlock', false, `<${value}>`);
    } else {
      document.execCommand(command, false, value);
    }

    // Update active formats after command
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      handleSelectionChange(rect);
    }
  }, [handleSelectionChange]);

  // State for marker ID (used to track where to insert inline image)
  const [inlineMarkerInfo, setInlineMarkerInfo] = useState<{ markerId: string; blockId: string } | null>(null);

  // Handle context menu (right-click) for inline image insertion
  // IMPORTANT: Insert the marker IMMEDIATELY while selection is still valid
  const handleContextMenu = useCallback((e: React.MouseEvent, blockId: string) => {
    e.preventDefault();

    // Get the current selection IMMEDIATELY
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);

    // Create and insert marker IMMEDIATELY while selection is valid
    const markerId = `inline-img-marker-${Date.now()}`;
    const marker = document.createElement('span');
    marker.id = markerId;
    marker.setAttribute('data-inline-marker', 'true');
    marker.className = 'inline-image-marker';
    marker.style.cssText = 'display:inline-block;background:#388AEB;width:8px;height:16px;vertical-align:middle;border-radius:2px;margin:0 2px;';
    marker.textContent = '|'; // Use text content instead of &nbsp; for easier debugging

    try {
      // Insert marker at cursor position
      range.insertNode(marker);

      // Update block content with the marker - but DON'T sync to HTML yet
      const contentElement = marker.closest('[contenteditable]');
      if (contentElement) {
        const newContent = contentElement.innerHTML;

        const blockIndex = blocks.findIndex(b => b.id === blockId);
        if (blockIndex !== -1) {
          // Store marker info FIRST before updating blocks
          setInlineMarkerInfo({ markerId, blockId });

          const newBlocks = blocks.map((block, idx) =>
            idx === blockIndex ? { ...block, content: newContent } : block
          );
          setBlocks(newBlocks);
        }
      }
    } catch (err) {
      // Error inserting marker
    }

    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setToolbarPosition(null);
  }, [blocks]);

  // Close context menu and remove marker if not used
  const closeContextMenu = useCallback(() => {
    // Remove marker if it exists and we're just closing the menu
    if (inlineMarkerInfo) {
      const marker = document.getElementById(inlineMarkerInfo.markerId);
      if (marker) {
        marker.remove();
        // Update state to remove marker from content
        const blockIndex = blocks.findIndex(b => b.id === inlineMarkerInfo.blockId);
        if (blockIndex !== -1) {
          const allEditables = document.querySelectorAll('.ce-block--editable [contenteditable]');
          const editableElement = allEditables[blockIndex];
          if (editableElement) {
            const newContent = editableElement.innerHTML;
            const newBlocks = blocks.map((b, idx) =>
              idx === blockIndex ? { ...b, content: newContent } : b
            );
            setBlocks(newBlocks);
            syncToHtml(newBlocks);
          }
        }
      }
      setInlineMarkerInfo(null);
    }
    setContextMenuPos(null);
  }, [blocks, inlineMarkerInfo, syncToHtml]);

  // Handle inline image insertion from context menu
  const handleInsertInlineImage = useCallback((position: 'left' | 'right' | 'center') => {
    setInlineImagePosition(position);
    // Don't close context menu yet - just open selector
    // The marker is already in place from handleContextMenu
    setContextMenuPos(null);
    setSelectorOpen(true);
  }, [inlineMarkerInfo]);

  // Handle inline image selection - replaces the marker with the actual image
  const handleInlineImageSelect = useCallback(
    (image: InsertableImage) => {
      // Check if we have a marker to replace
      if (!inlineMarkerInfo) {
        // Fallback to block insertion if no marker
        const imageHtml = createImageHtml(image);
        const newBlock: ContentBlock = {
          id: `block-img-${Date.now()}`,
          type: 'image',
          content: imageHtml,
          imageOrigin: image.origin,
          wpId: image.wpId,
        };
        const newBlocks = [...blocks];
        newBlocks.splice(insertAtIndex, 0, newBlock);
        setBlocks(newBlocks);
        syncToHtml(newBlocks);
        setSelectorOpen(false);
        return;
      }

      // Create inline image HTML with position class
      const positionClass = inlineImagePosition === 'left' ? 'float-left'
        : inlineImagePosition === 'right' ? 'float-right'
        : 'float-none';

      const maxWidth = inlineImagePosition === 'center' ? '100%' : '45%';

      // STRATEGY 1: Try to find marker in DOM first (most reliable)
      const markerInDOM = document.getElementById(inlineMarkerInfo.markerId);

      if (markerInDOM) {
        // Create simple image element (no wrapper needed)
        const imgElement = createInlineImageElement(
          image.src,
          image.alt || '',
          image.title || '',
          image.origin,
          image.wpId,
          positionClass,
          maxWidth
        );

        // Replace marker with image in DOM
        markerInDOM.parentNode?.replaceChild(imgElement, markerInDOM);

        // Get updated content from the contenteditable element
        let contentElement: Element | null = imgElement.closest('[contenteditable="true"]');
        if (!contentElement) {
          const blockElement = document.querySelector(`[data-block-id="${inlineMarkerInfo.blockId}"]`);
          contentElement = blockElement?.querySelector('[contenteditable="true"]') || null;
        }

        if (contentElement) {
          const newContent = contentElement.innerHTML;

          // Find which block this belongs to
          const blockIndex = blocks.findIndex(b => b.id === inlineMarkerInfo.blockId);
          if (blockIndex !== -1) {
            const newBlocks = blocks.map((b, idx) =>
              idx === blockIndex ? { ...b, content: newContent } : b
            );
            setBlocks(newBlocks);
            syncToHtml(newBlocks);
          }
        }
      } else {
        // STRATEGY 2: Try to find marker in block content with regex
        // Create simple img HTML for regex replacement (no wrapper/badge)
        const inlineImgHtml = `<img src="${image.src}" alt="${image.alt || ''}" title="${image.title || ''}" data-origin="${image.origin}" ${image.wpId ? `data-wp-id="${image.wpId}"` : ''} class="ce-inline-image ${positionClass}" style="max-width: ${maxWidth}; border-radius: 8px;" draggable="true" />`;

        const blockIndex = blocks.findIndex(b => b.id === inlineMarkerInfo.blockId);
        if (blockIndex !== -1) {
          const block = blocks[blockIndex];

          // More flexible regex that matches the marker span
          const markerRegex = new RegExp(`<span[^>]*id=["']?${inlineMarkerInfo.markerId}["']?[^>]*>[^<]*</span>`, 'gi');
          let newContent = block.content.replace(markerRegex, inlineImgHtml);

          if (newContent !== block.content) {
            const newBlocks = blocks.map((b, idx) =>
              idx === blockIndex ? { ...b, content: newContent } : b
            );
            setBlocks(newBlocks);
            syncToHtml(newBlocks);
          }
        }
      }

      setSelectorOpen(false);
      setInlineMarkerInfo(null);
    },
    [blocks, inlineMarkerInfo, inlineImagePosition, insertAtIndex, syncToHtml]
  );

  // Clean up marker if selector is closed without selecting an image
  const handleSelectorClose = useCallback(() => {
    if (inlineMarkerInfo) {
      // Try to remove marker from DOM first
      const marker = document.getElementById(inlineMarkerInfo.markerId);
      if (marker) {
        marker.remove();
      }

      // Remove the marker from the content
      const blockIndex = blocks.findIndex(b => b.id === inlineMarkerInfo.blockId);
      if (blockIndex !== -1) {
        // Get fresh content from DOM
        const allEditables = document.querySelectorAll('.ce-block--editable [contenteditable]');
        const editableElement = allEditables[blockIndex];
        if (editableElement) {
          const newContent = editableElement.innerHTML;
          const newBlocks = blocks.map((b, idx) =>
            idx === blockIndex ? { ...b, content: newContent } : b
          );
          setBlocks(newBlocks);
          syncToHtml(newBlocks);
        }
      }
      setInlineMarkerInfo(null);
    }
    setSelectorOpen(false);
    setIsInsertingFeatured(false);
    setReplaceBlockId(null);
  }, [blocks, inlineMarkerInfo, syncToHtml]);

  // Handle click on inline images within text
  const handleInlineImageClick = useCallback((e: React.MouseEvent, blockId: string) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('ce-inline-image')) {
      e.preventDefault();
      e.stopPropagation();

      const img = target as HTMLImageElement;
      const rect = img.getBoundingClientRect();

      // Add selected class
      document.querySelectorAll('.ce-inline-image.selected').forEach(el => el.classList.remove('selected'));
      img.classList.add('selected');

      setSelectedInlineImage({
        element: img,
        blockId,
        position: {
          x: rect.left + rect.width / 2,
          y: rect.top,
        },
      });
      setToolbarPosition(null);
    }
  }, []);

  // Get current position of selected inline image
  const getInlineImagePosition = useCallback((): 'left' | 'right' | 'center' => {
    if (!selectedInlineImage) return 'left';
    const img = selectedInlineImage.element;
    if (img.classList.contains('float-right')) return 'right';
    if (img.classList.contains('float-none')) return 'center';
    return 'left';
  }, [selectedInlineImage]);

  // Change position of selected inline image
  const handleChangeInlineImagePosition = useCallback((newPosition: 'left' | 'right' | 'center') => {
    if (!selectedInlineImage) return;

    const img = selectedInlineImage.element;
    const blockId = selectedInlineImage.blockId;

    // Remove existing position classes
    img.classList.remove('float-left', 'float-right', 'float-none');

    // Add new position class
    const positionClass = newPosition === 'left' ? 'float-left'
      : newPosition === 'right' ? 'float-right'
      : 'float-none';
    img.classList.add(positionClass);

    // Update style for centered images
    img.style.maxWidth = newPosition === 'center' ? '100%' : '45%';

    // Also update wrapper position class if it exists
    const wrapper = img.closest('.ce-inline-image-wrapper');
    if (wrapper) {
      wrapper.classList.remove('float-left', 'float-right', 'float-none');
      wrapper.classList.add(positionClass);
    }

    // Update block content - find the correct contenteditable (not the wrapper)
    let contentElement: Element | null = img.closest('[contenteditable="true"]');
    if (!contentElement) {
      const blockElement = document.querySelector(`[data-block-id="${blockId}"]`);
      contentElement = blockElement?.querySelector('[contenteditable="true"]') || null;
    }

    if (contentElement) {
      const newContent = contentElement.innerHTML;
      const newBlocks = blocks.map(block =>
        block.id === blockId ? { ...block, content: newContent } : block
      );
      setBlocks(newBlocks);
      syncToHtml(newBlocks);
    }

    // Update position for controls
    const rect = img.getBoundingClientRect();
    setSelectedInlineImage({
      ...selectedInlineImage,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top,
      },
    });
  }, [selectedInlineImage, blocks, syncToHtml]);

  // Delete selected inline image
  const handleDeleteInlineImage = useCallback(() => {
    if (!selectedInlineImage) return;

    const img = selectedInlineImage.element;
    const blockId = selectedInlineImage.blockId;

    // Remove the image from DOM
    img.remove();

    // Find the block and get updated content
    const blockIndex = blocks.findIndex(b => b.id === blockId);
    if (blockIndex !== -1) {
      // Need to find the correct contenteditable element
      const allEditables = document.querySelectorAll('.ce-block--editable [contenteditable]');
      const editableElement = allEditables[blockIndex];
      if (editableElement) {
        const newContent = editableElement.innerHTML;
        const newBlocks = blocks.map((block, idx) =>
          idx === blockIndex ? { ...block, content: newContent } : block
        );
        setBlocks(newBlocks);
        syncToHtml(newBlocks);
      }
    }

    setSelectedInlineImage(null);
  }, [selectedInlineImage, blocks, syncToHtml]);

  // Close inline image controls
  const closeInlineImageControls = useCallback(() => {
    if (selectedInlineImage) {
      selectedInlineImage.element.classList.remove('selected');
    }
    setSelectedInlineImage(null);
    setShowInlineMetadataPanel(false);
  }, [selectedInlineImage]);

  // Get inline image metadata
  const getInlineImageMetadata = useCallback(() => {
    if (!selectedInlineImage) return { title: '', alt: '', src: '' };
    const img = selectedInlineImage.element;
    return {
      title: img.getAttribute('title') || '',
      alt: img.getAttribute('alt') || '',
      src: img.getAttribute('src') || '',
    };
  }, [selectedInlineImage]);

  // Check if inline image has metadata
  const inlineImageHasMetadata = useCallback(() => {
    if (!selectedInlineImage) return false;
    const img = selectedInlineImage.element;
    return !!(img.getAttribute('title') || img.getAttribute('alt'));
  }, [selectedInlineImage]);

  // Handle opening metadata panel for inline image
  const handleOpenInlineMetadataPanel = useCallback(() => {
    setShowInlineMetadataPanel(true);
  }, []);

  // Handle saving inline image metadata
  const handleSaveInlineImageMetadata = useCallback((newTitle: string, newAlt: string) => {
    if (!selectedInlineImage) return;

    const img = selectedInlineImage.element;
    const blockId = selectedInlineImage.blockId;

    // Update image attributes in DOM
    img.setAttribute('title', newTitle);
    img.setAttribute('alt', newAlt);

    // Update the badge to reflect new metadata status
    updateInlineImageBadge(img);

    // Find the correct contenteditable element (not the wrapper which has contenteditable="false")
    // We need to find the parent that has contenteditable="true" (the actual text block)
    let contentElement: Element | null = img.closest('[contenteditable="true"]');

    // If not found, try finding by block ID
    if (!contentElement) {
      const blockElement = document.querySelector(`[data-block-id="${blockId}"]`);
      contentElement = blockElement?.querySelector('[contenteditable="true"]') || null;
    }

    if (contentElement) {
      const newContent = contentElement.innerHTML;

      const newBlocks = blocks.map(block =>
        block.id === blockId ? { ...block, content: newContent } : block
      );
      setBlocks(newBlocks);
      syncToHtml(newBlocks);
    }

    setShowInlineMetadataPanel(false);
  }, [selectedInlineImage, blocks, syncToHtml]);

  // ===============================
  // NATIVE DRAG & DROP FOR INLINE IMAGES
  // ===============================
  const [draggingInlineImage, setDraggingInlineImage] = useState<{
    element: HTMLImageElement;
    blockId: string;
    originalHtml: string;
  } | null>(null);

  const dropIndicatorRef = useRef<HTMLSpanElement | null>(null);

  // Handle drag start for inline images
  const handleInlineImageDragStart = useCallback((e: React.DragEvent, blockId: string) => {
    const target = e.target as HTMLElement;
    if (!target.classList.contains('ce-inline-image')) return;

    const img = target as HTMLImageElement;
    img.classList.add('dragging');

    // Store the image info
    setDraggingInlineImage({
      element: img,
      blockId,
      originalHtml: img.outerHTML,
    });

    // Set drag data
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', img.outerHTML);

    // Create custom drag image
    const dragImage = img.cloneNode(true) as HTMLImageElement;
    dragImage.style.width = '150px';
    dragImage.style.height = 'auto';
    dragImage.style.opacity = '0.8';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 75, 50);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  }, []);

  // Handle drag over - show drop indicator
  const handleInlineImageDragOver = useCallback((e: React.DragEvent, _blockId: string) => {
    if (!draggingInlineImage) return;

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const contentEditable = (e.currentTarget as HTMLElement).querySelector('[contenteditable]');
    if (!contentEditable) return;

    // Add drag-over class to the block
    (e.currentTarget as HTMLElement).classList.add('drag-over');

    // Get caret position from mouse coordinates
    const range = document.caretRangeFromPoint(e.clientX, e.clientY);
    if (!range) return;

    // Check if we're inside the contenteditable
    if (!contentEditable.contains(range.startContainer)) return;

    // Remove existing drop indicator
    if (dropIndicatorRef.current && dropIndicatorRef.current.parentNode) {
      dropIndicatorRef.current.remove();
    }

    // Create new drop indicator
    const indicator = document.createElement('span');
    indicator.className = 'ce-drop-indicator';
    indicator.setAttribute('data-drop-indicator', 'true');
    dropIndicatorRef.current = indicator;

    // Insert indicator at caret position
    try {
      range.insertNode(indicator);
    } catch (err) {
      // Could not insert drop indicator
    }
  }, [draggingInlineImage]);

  // Handle drag leave
  const handleInlineImageDragLeave = useCallback((e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).classList.remove('drag-over');

    // Only remove indicator if we're actually leaving the block
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      if (dropIndicatorRef.current && dropIndicatorRef.current.parentNode) {
        dropIndicatorRef.current.remove();
        dropIndicatorRef.current = null;
      }
    }
  }, []);

  // Handle drop - move the image to new position
  const handleInlineImageDrop = useCallback((e: React.DragEvent, targetBlockId: string) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement).classList.remove('drag-over');

    if (!draggingInlineImage) return;

    const { element: draggedImg, blockId: sourceBlockId } = draggingInlineImage;

    // Find the drop indicator
    const indicator = dropIndicatorRef.current;
    if (!indicator || !indicator.parentNode) {
      draggedImg.classList.remove('dragging');
      setDraggingInlineImage(null);
      return;
    }

    // Create new image element to insert
    const newImg = document.createElement('img');
    newImg.src = draggedImg.src;
    newImg.alt = draggedImg.alt;
    newImg.title = draggedImg.title;
    newImg.className = draggedImg.className.replace('dragging', '').trim();
    newImg.style.cssText = draggedImg.style.cssText;
    // Copy data attributes
    if (draggedImg.getAttribute('data-origin')) {
      newImg.setAttribute('data-origin', draggedImg.getAttribute('data-origin')!);
    }
    if (draggedImg.getAttribute('data-wp-id')) {
      newImg.setAttribute('data-wp-id', draggedImg.getAttribute('data-wp-id')!);
    }
    newImg.setAttribute('draggable', 'true');

    // Replace indicator with new image
    indicator.parentNode.replaceChild(newImg, indicator);
    dropIndicatorRef.current = null;

    // Remove original image from source
    draggedImg.remove();

    // Update blocks state
    // Get all editable blocks and update their content
    const newBlocks = blocks.map(block => {
      if (block.id === targetBlockId) {
        const targetEl = document.querySelector(`[data-block-id="${targetBlockId}"] [contenteditable]`);
        if (targetEl) {
          return { ...block, content: targetEl.innerHTML };
        }
      }
      if (block.id === sourceBlockId && sourceBlockId !== targetBlockId) {
        const sourceEl = document.querySelector(`[data-block-id="${sourceBlockId}"] [contenteditable]`);
        if (sourceEl) {
          return { ...block, content: sourceEl.innerHTML };
        }
      }
      return block;
    });

    setBlocks(newBlocks);
    syncToHtml(newBlocks);

    draggedImg.classList.remove('dragging');
    setDraggingInlineImage(null);
  }, [draggingInlineImage, blocks, syncToHtml]);

  // Handle drag end (cleanup)
  const handleInlineImageDragEnd = useCallback((_e: React.DragEvent) => {
    // Remove dragging class from all images
    document.querySelectorAll('.ce-inline-image.dragging').forEach(el => {
      el.classList.remove('dragging');
    });

    // Remove drag-over class from all blocks
    document.querySelectorAll('.drag-over').forEach(el => {
      el.classList.remove('drag-over');
    });

    // Remove drop indicator
    if (dropIndicatorRef.current && dropIndicatorRef.current.parentNode) {
      dropIndicatorRef.current.remove();
      dropIndicatorRef.current = null;
    }

    setDraggingInlineImage(null);
  }, []);

  const activeBlock = activeId ? blocks.find((b) => b.id === activeId) : null;
  const imageBlocks = blocks.filter((b) => b.type === 'image');

  return (
    <div className="ce-editor max-w-4xl mx-auto px-16 py-8">
      {/* Header */}
      <div className="mb-8 pb-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Editor de Contenido</h3>
              <p className="text-sm text-gray-500">
                {imageBlocks.length === 0
                  ? 'Usa los botones + para añadir imágenes'
                  : `${imageBlocks.length} imagen${imageBlocks.length !== 1 ? 'es' : ''} en el artículo`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Image Button - Only visible when no featured image exists */}
      {(() => {
        // Check if first block is an image (featured image is always at index 0)
        const hasFeaturedImage = blocks.length > 0 && blocks[0].type === 'image';

        // Only show button if there's no featured image yet
        if (hasFeaturedImage) return null;

        return (
          <button
            onClick={handleInsertFeatured}
            className="ce-add-block ce-add-block--featured w-full"
          >
            <Star className="w-5 h-5" />
            <span>Añadir Imagen Destacada</span>
          </button>
        );
      })()}

      {/* DnD Context */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={blocks.map((b) => b.id)}
          strategy={verticalListSortingStrategy}
        >
          {blocks.map((block, index) => {
            // Calculate image index (1-based) for this block
            const imageBlocksBeforeThis = blocks.slice(0, index).filter(b => b.type === 'image').length;
            const currentImageIndex = block.type === 'image' ? imageBlocksBeforeThis + 1 : 0;

            // Build article context from text blocks for AI generation
            const articleContextText = blocks
              .filter(b => b.type === 'text' || b.type === 'heading')
              .map(b => b.content.replace(/<[^>]+>/g, ''))
              .join(' ')
              .substring(0, 2000);

            return (
              <React.Fragment key={block.id}>
                <SortableBlock
                  block={block}
                  index={index}
                  totalBlocks={blocks.length}
                  imageIndex={currentImageIndex}
                  onDelete={handleDelete}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                  onInsertAfter={handleInsertAfter}
                  onContentChange={handleContentChange}
                  onSelectionChange={handleSelectionChange}
                  onContextMenu={handleContextMenu}
                  onInlineImageClick={handleInlineImageClick}
                  onInlineImageDragStart={handleInlineImageDragStart}
                  onInlineImageDragOver={handleInlineImageDragOver}
                  onInlineImageDragLeave={handleInlineImageDragLeave}
                  onInlineImageDrop={handleInlineImageDrop}
                  onInlineImageDragEnd={handleInlineImageDragEnd}
                  articleTitle={articleTitle || ''}
                  articleContext={articleContextText}
                  onUpdateImageMetadata={handleUpdateImageMetadata}
                  onReplaceFeatured={handleReplaceFeatured}
                />
              </React.Fragment>
            );
          })}
        </SortableContext>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeBlock ? <DragOverlayContent block={activeBlock} /> : null}
        </DragOverlay>
      </DndContext>

      {/* Empty state */}
      {blocks.length === 0 && (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <ImageIcon className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-lg">No hay contenido para mostrar</p>
          <p className="text-gray-400 text-sm mt-1">
            El contenido del artículo aparecerá aquí
          </p>
        </div>
      )}

      {/* Floating Toolbar for text formatting */}
      <FloatingToolbar
        position={toolbarPosition}
        onFormat={handleFormat}
        activeFormats={activeFormats}
      />

      {/* Context Menu for inline image insertion */}
      <ContextMenu
        position={contextMenuPos}
        onInsertInline={handleInsertInlineImage}
        onClose={closeContextMenu}
      />

      {/* Inline Image Controls - shows when clicking an inline image */}
      {selectedInlineImage && (
        <>
          <InlineImageControls
            position={selectedInlineImage.position}
            currentPosition={getInlineImagePosition()}
            onChangePosition={handleChangeInlineImagePosition}
            onDelete={handleDeleteInlineImage}
            onClose={closeInlineImageControls}
            onEditMetadata={handleOpenInlineMetadataPanel}
            hasMetadata={inlineImageHasMetadata()}
            metadataPanelOpen={showInlineMetadataPanel}
          />
          {/* Inline Image Metadata Panel */}
          {showInlineMetadataPanel && (
            <div
              style={{
                position: 'fixed',
                left: selectedInlineImage.position.x,
                top: selectedInlineImage.position.y + 10,
                transform: 'translateX(-50%)',
                zIndex: 1000,
              }}
            >
              <ImageMetadataPanel
                blockId={selectedInlineImage.blockId}
                currentTitle={getInlineImageMetadata().title}
                currentAlt={getInlineImageMetadata().alt}
                articleTitle={articleTitle || ''}
                articleContext={blocks
                  .filter(b => b.type === 'text' || b.type === 'heading')
                  .map(b => b.content.replace(/<[^>]+>/g, ''))
                  .join(' ')
                  .substring(0, 2000)}
                imageIndex={1}
                imageSrc={getInlineImageMetadata().src}
                onSave={handleSaveInlineImageMetadata}
                onClose={() => setShowInlineMetadataPanel(false)}
              />
            </div>
          )}
        </>
      )}

      {/* Image Source Selector Modal */}
      <ImageSourceSelector
        isOpen={selectorOpen}
        onClose={handleSelectorClose}
        onSelect={inlineMarkerInfo ? handleInlineImageSelect : handleImageSelect}
        projectId={projectId}
        sessionId={sessionId}
        articleTitle={articleTitle}
        brief={brief}
        isFeaturedImage={isInsertingFeatured}
      />
    </div>
  );
};
